import { useState, useEffect, useCallback } from 'react';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDoc,
  serverTimestamp,
  getDocs
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { DocumentFolder, FolderTreeNode } from '../types/documentStorage';
import { useAuth } from '../contexts/AuthContext';
import { useCustomers } from './useCustomers';
import toast from 'react-hot-toast';

export function useDocumentFolders() {
  const [folders, setFolders] = useState<DocumentFolder[]>([]);
  const [folderTree, setFolderTree] = useState<FolderTreeNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>('root');
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set(['root']));
  
  const { user } = useAuth();
  const { customers } = useCustomers();

  // Load folders from Firebase
  useEffect(() => {
    if (!user?.organizationId) {
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'documentFolders'),
      where('organizationId', '==', user.organizationId),
      where('isActive', '==', true),
      orderBy('name', 'asc')
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      try {
        const folderData: DocumentFolder[] = [];
        
        for (const doc of snapshot.docs) {
          const data = doc.data();
          folderData.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
          } as DocumentFolder);
        }

        // console.log('📁 Loaded folders:', folderData.map(f => f.name));

        setFolders(folderData);
        setLoading(false);
      } catch (error) {
        console.error('Error loading folders:', error);
        toast.error('Hiba a mappák betöltésekor');
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [user?.organizationId]);

  // Build folder tree structure
  const buildTree = useCallback(async () => {
    return await buildFolderTreeWithStats(folders);
  }, [folders, customers, expandedFolders, selectedFolderId, user?.organizationId]);

  useEffect(() => {
    const loadTree = async () => {
      try {
        const tree = await buildTree();
        setFolderTree(tree);
      } catch (error) {
        console.error('Error building folder tree:', error);
        // Fallback to synchronous version
        const fallbackTree = buildFolderTree(folders);
        setFolderTree(fallbackTree);
      }
    };
    
    loadTree();
  }, [buildTree]);

  // Auto-refresh folder statistics when folders change (optional - user can manually refresh)
  // Removed automatic refresh to avoid unnecessary API calls

  // No default folder initialization - user creates all folders manually

  // Calculate folder statistics from documents
  const calculateFolderStats = async (folderId: string | undefined, customerId?: string): Promise<{count: number, size: number}> => {
    if (!user?.organizationId) return {count: 0, size: 0};

    try {
      let q;
      if (customerId) {
        // Count documents for specific customer
        q = query(
          collection(db, 'documentStorage'),
          where('organizationId', '==', user.organizationId),
          where('customerId', '==', customerId),
          where('status', '==', 'active')
        );
      } else if (folderId && folderId.startsWith('customer-')) {
        // Customer folder - use customerId from folderId
        const actualCustomerId = folderId.replace('customer-', '');
        q = query(
          collection(db, 'documentStorage'),
          where('organizationId', '==', user.organizationId),
          where('customerId', '==', actualCustomerId),
          where('status', '==', 'active')
        );
      } else if (folderId === undefined) {
        // "Minden dokumentum" - összes dokumentum lekérése
        q = query(
          collection(db, 'documentStorage'),
          where('organizationId', '==', user.organizationId),
          where('status', '==', 'active')
        );
      } else {
        // Regular folder - use folderId (if folderId field exists)
        // For now, let's try to get all documents and filter later
        // since folderId might not exist in all documents yet
        q = query(
          collection(db, 'documentStorage'),
          where('organizationId', '==', user.organizationId),
          where('status', '==', 'active')
        );
      }

      // console.log(`🔍 Querying documents for folderId: ${folderId}, customerId: ${customerId}`);
      const snapshot = await getDocs(q);
      let count = 0;
      let size = 0;

      snapshot.forEach(docSnapshot => {
        const data = docSnapshot.data();
        
        if (folderId === undefined) {
          // "Minden dokumentum" - minden dokumentumot számolunk
          count++;
          size += data.fileSize || 0;
          // console.log(`📄 Found document for "Minden dokumentum":`, data.title, data.fileSize);
        } else if (!customerId && folderId && !folderId.startsWith('customer-')) {
          // For regular folders, check if the document has the matching folderId
          if (data.folderId === folderId) {
            count++;
            size += data.fileSize || 0;
            // console.log(`📄 Found document for folder ${folderId}:`, data.title, data.fileSize);
          }
        } else {
          // For customer-based queries, count all documents for that customer
          count++;
          size += data.fileSize || 0;
          // console.log(`📄 Found document for customer ${customerId}:`, data.title, data.fileSize);
        }
      });

      // console.log(`📊 Final stats for ${folderId}: ${count} documents, ${size} bytes`);
      return {count, size};
    } catch (error) {
      console.error('Error calculating folder stats:', error);
      return {count: 0, size: 0};
    }
  };


  // Build hierarchical folder tree with real statistics
  const buildFolderTreeWithStats = async (folders: DocumentFolder[]): Promise<FolderTreeNode[]> => {
    const folderMap = new Map<string, FolderTreeNode>();
    const rootFolders: FolderTreeNode[] = [];

    // Convert folders to tree nodes with direct statistics (only documents in this folder)
    for (const folder of folders) {
      // Calculate direct statistics for each folder (only documents directly in this folder)
      const stats = await calculateFolderStats(folder.id, folder.customerId);
      // console.log(`📊 Folder "${folder.name}" direct stats:`, stats);
      
      const node: FolderTreeNode = {
        ...folder,
        documentCount: stats.count, // Direct documents only
        totalSize: stats.size,      // Direct size only
        children: [],
        isExpanded: expandedFolders.has(folder.id),
        isSelected: selectedFolderId === folder.id
      };
      folderMap.set(folder.id, node);
    }

    // Build tree structure
    folderMap.forEach(node => {
      if (node.parentId && folderMap.has(node.parentId)) {
        folderMap.get(node.parentId)!.children.push(node);
      } else {
        rootFolders.push(node);
      }
    });

    // Calculate hierarchical stats (including children) for all nodes
    const updateHierarchicalStats = (node: FolderTreeNode) => {
      // First update all children recursively
      if (node.children && node.children.length > 0) {
        node.children.forEach(updateHierarchicalStats);
        
        // After children are updated, add their stats to this node
        let childrenCount = 0;
        let childrenSize = 0;
        
        node.children.forEach(child => {
          childrenCount += child.documentCount;
          childrenSize += child.totalSize;
        });
        
        // Update this node to include children's stats
        const originalCount = node.documentCount;
        const originalSize = node.totalSize;
        
        node.documentCount = originalCount + childrenCount;
        node.totalSize = originalSize + childrenSize;
        
        // console.log(`📊 Folder "${node.name}" - Direct: ${originalCount} docs/${originalSize} bytes, Children: ${childrenCount} docs/${childrenSize} bytes, Total: ${node.documentCount} docs/${node.totalSize} bytes`);
      } else {
        // console.log(`📊 Folder "${node.name}" - No children, Total: ${node.documentCount} docs/${node.totalSize} bytes`);
      }
    };

    // Update all root folders with hierarchical stats
    rootFolders.forEach(updateHierarchicalStats);

    // Hozzáadunk egy virtuális "Minden dokumentum" gyökér mappát
    const allDocumentsStats = await calculateFolderStats(undefined, undefined); // undefined = minden dokumentum
    const allDocumentsFolder: FolderTreeNode = {
      id: 'root',
      name: 'Minden dokumentum',
      description: 'Összes dokumentum (beleértve a gyökér dokumentumokat)',
      parentId: undefined,
      customerId: undefined,
      customerName: undefined,
      color: '#8b5cf6', // purple-500
      icon: 'FolderIcon',
      organizationId: user?.organizationId || '',
      isDefault: true,
      isActive: true,
      documentCount: allDocumentsStats.count,
      totalSize: allDocumentsStats.size,
      createdBy: user?.uid || '',
      createdByName: user?.displayName || 'System',
      createdAt: new Date(),
      updatedAt: new Date(),
      path: [],
      level: 0,
      children: [],
      isExpanded: expandedFolders.has('root'),
      isSelected: selectedFolderId === 'root'
    };

    // Sort folders
    const sortFolders = (folders: FolderTreeNode[]) => {
      folders.sort((a, b) => {
        // Default folders first, then by name
        if (a.isDefault && !b.isDefault) return -1;
        if (!a.isDefault && b.isDefault) return 1;
        return a.name.localeCompare(b.name);
      });
      folders.forEach(folder => {
        if (folder.children.length > 0) {
          sortFolders(folder.children);
        }
      });
    };

    // A "Minden dokumentum" mappa legyen az első
    const finalFolders = [allDocumentsFolder, ...rootFolders];
    sortFolders(finalFolders);
    return finalFolders;
  };

  // Build hierarchical folder tree (synchronous fallback)
  const buildFolderTree = (folders: DocumentFolder[]): FolderTreeNode[] => {
    const folderMap = new Map<string, FolderTreeNode>();
    const rootFolders: FolderTreeNode[] = [];

    // Convert folders to tree nodes
    folders.forEach(folder => {
      const node: FolderTreeNode = {
        ...folder,
        children: [],
        isExpanded: expandedFolders.has(folder.id),
        isSelected: selectedFolderId === folder.id
      };
      folderMap.set(folder.id, node);
    });

    // Build tree structure
    folderMap.forEach(node => {
      if (node.parentId && folderMap.has(node.parentId)) {
        folderMap.get(node.parentId)!.children.push(node);
      } else {
        rootFolders.push(node);
      }
    });

    // No automatic customer folders - user creates everything manually

    // Sort folders
    const sortFolders = (folders: FolderTreeNode[]) => {
      folders.sort((a, b) => {
        // Default folders first, then by name
        if (a.isDefault && !b.isDefault) return -1;
        if (!a.isDefault && b.isDefault) return 1;
        return a.name.localeCompare(b.name);
      });
      folders.forEach(folder => {
        if (folder.children.length > 0) {
          sortFolders(folder.children);
        }
      });
    };

    sortFolders(rootFolders);
    return rootFolders;
  };

  // Create new folder
  const createFolder = async (folderData: {
    name: string;
    description?: string;
    parentId?: string;
    customerId?: string;
    color?: string;
    icon?: string;
  }) => {
    if (!user?.organizationId) return null;

    try {
      const parentFolder = folderData.parentId ? folders.find(f => f.id === folderData.parentId) : null;
      const level = parentFolder ? parentFolder.level + 1 : 0;
      const path = parentFolder ? [...parentFolder.path, folderData.name] : [folderData.name];

      const newFolderData = {
        name: folderData.name,
        description: folderData.description || '',
        parentId: folderData.parentId || null,
        customerId: folderData.customerId || null,
        customerName: folderData.customerId ? customers.find(c => c.id === folderData.customerId)?.companyName || customers.find(c => c.id === folderData.customerId)?.firstName + ' ' + customers.find(c => c.id === folderData.customerId)?.lastName || '' : '',
        color: folderData.color || '#6b7280',
        icon: folderData.icon || 'FolderIcon',
        organizationId: user.organizationId,
        isDefault: false,
        isActive: true,
        documentCount: 0,
        totalSize: 0,
        createdBy: user.id,
        createdByName: user.displayName || user.email || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        path,
        level
      };

      const docRef = await addDoc(collection(db, 'documentFolders'), newFolderData);
      toast.success(`"${folderData.name}" mappa létrehozva`);
      return docRef.id;
    } catch (error) {
      console.error('Error creating folder:', error);
      toast.error('Hiba a mappa létrehozásakor');
      return null;
    }
  };

  // Update folder
  const updateFolder = async (folderId: string, updates: Partial<DocumentFolder>) => {
    try {
      const folderRef = doc(db, 'documentFolders', folderId);
      await updateDoc(folderRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      toast.success('Mappa frissítve');
    } catch (error) {
      console.error('Error updating folder:', error);
      toast.error('Hiba a mappa frissítésekor');
    }
  };

  // Delete folder
  const deleteFolder = async (folderId: string) => {
    try {
      const folderRef = doc(db, 'documentFolders', folderId);
      await updateDoc(folderRef, {
        isActive: false,
        updatedAt: serverTimestamp()
      });
      toast.success('Mappa törölve');
    } catch (error) {
      console.error('Error deleting folder:', error);
      toast.error('Hiba a mappa törlésekor');
    }
  };

  // Move folder to a new parent
  const moveFolder = async (folderId: string, newParentId: string | null) => {
    try {
      const folderRef = doc(db, 'documentFolders', folderId);
      
      // Get the folder to update its path
      const folderDoc = await getDoc(folderRef);
      
      if (!folderDoc.exists()) {
        throw new Error('Mappa nem található');
      }

      const folderData = folderDoc.data();
      let newPath: string[] = [];
      let newLevel = 0;

      // Calculate new path and level
      if (newParentId) {
        const parentRef = doc(db, 'documentFolders', newParentId);
        const parentDoc = await getDoc(parentRef);
        
        if (parentDoc.exists()) {
          const parentData = parentDoc.data();
          newPath = [...(parentData.path || []), folderData.name];
          newLevel = (parentData.level || 0) + 1;
        }
      } else {
        // Moving to root level
        newPath = [folderData.name];
        newLevel = 0;
      }

      await updateDoc(folderRef, {
        parentId: newParentId,
        path: newPath,
        level: newLevel,
        updatedAt: serverTimestamp()
      });

      // Update all child folders' paths recursively
      await updateChildFolderPaths(folderId, newPath, newLevel);

      toast.success('Mappa sikeresen áthelyezve');
    } catch (error) {
      console.error('Error moving folder:', error);
      toast.error('Hiba a mappa áthelyezésekor');
    }
  };

  // Update child folder paths recursively
  const updateChildFolderPaths = async (parentId: string, parentPath: string[], parentLevel: number) => {
    try {
      const childQuery = query(
        collection(db, 'documentFolders'),
        where('parentId', '==', parentId),
        where('organizationId', '==', user?.organizationId),
        where('isActive', '==', true)
      );
      
      const childFolders = await getDocs(childQuery);

      const updatePromises = childFolders.docs.map(async (childDoc) => {
        const childData = childDoc.data();
        const newChildPath = [...parentPath, childData.name];
        const newChildLevel = parentLevel + 1;

        await updateDoc(childDoc.ref, {
          path: newChildPath,
          level: newChildLevel,
          updatedAt: serverTimestamp()
        });

        // Recursively update grandchildren
        await updateChildFolderPaths(childDoc.id, newChildPath, newChildLevel);
      });

      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Error updating child folder paths:', error);
    }
  };

  // Toggle folder expansion
  const toggleFolderExpand = useCallback((folderId: string) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  }, []);

  // Select folder
  const selectFolder = useCallback((folderId: string | null) => {
    setSelectedFolderId(folderId);
  }, []);

  // Update folder statistics
  const updateFolderStats = async (folderId: string, customerId?: string) => {
    const stats = await calculateFolderStats(folderId, customerId);
    
    if (folderId.startsWith('customer-')) {
      // For customer folders, we don't update Firebase as they're virtual
      // The stats will be reflected in the tree building
      return;
    }
    
    try {
      const folderRef = doc(db, 'documentFolders', folderId);
      await updateDoc(folderRef, {
        documentCount: stats.count,
        totalSize: stats.size,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating folder stats:', error);
    }
  };

  // Refresh all folder statistics
  const refreshAllFolderStats = async () => {
    // console.log('🔄 Refreshing folder statistics...');
    
    // Update regular folders in Firebase
    for (const folder of folders) {
      await updateFolderStats(folder.id, folder.customerId);
    }
    
    // Rebuild the folder tree with fresh statistics
    try {
      const freshTree = await buildFolderTreeWithStats(folders);
      setFolderTree(freshTree);
      // console.log('✅ Folder tree refreshed with updated statistics');
    } catch (error) {
      console.error('❌ Error refreshing folder tree:', error);
      // Fallback to synchronous build
      const fallbackTree = buildFolderTree(folders);
      setFolderTree(fallbackTree);
    }
    
    // console.log('✅ Folder statistics refreshed');
  };

  // Get selected folder from tree (recursive search)
  const findFolderInTree = (nodes: FolderTreeNode[], id: string): FolderTreeNode | null => {
    for (const node of nodes) {
      if (node.id === id) return node;
      if (node.children && node.children.length > 0) {
        const found = findFolderInTree(node.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  const selectedFolder = selectedFolderId ? 
    findFolderInTree(folderTree, selectedFolderId)
    : null;

  return {
    folders,
    folderTree,
    loading,
    selectedFolder,
    selectedFolderId,
    expandedFolders,
    createFolder,
    updateFolder,
    deleteFolder,
    moveFolder,
    toggleFolderExpand,
    selectFolder,
    updateFolderStats,
    refreshAllFolderStats,
    calculateFolderStats
  };
}
